import axios from "axios";

// Always use relative `/api` paths so API calls go to the same origin that
// served the page. Emergent's ingress routes `/api/*` to the backend regardless
// of hostname — this works for the preview URL, the emergent.host deploy URL,
// and any custom domain pointed at the app, with zero CORS config needed.
export const API = "/api";

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Also attach bearer token if stored
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("nc_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ─── Cloudflare API helpers ───────────────────────────────────────────────────

export const cfApi = {
  listAccounts: () => api.get("/cloudflare/accounts"),
  addAccount: (body) => api.post("/cloudflare/accounts", body),
  updateAccount: (id, body) => api.put(`/cloudflare/accounts/${id}`, body),
  deleteAccount: (id) => api.delete(`/cloudflare/accounts/${id}`),
  testAccount: (id) => api.post(`/cloudflare/accounts/${id}/test`),

  listZones: () => api.get("/cloudflare/zones"),
  createZone: (body) => api.post("/cloudflare/zones/create", body),
  getZoneDnsRecords: (zoneId) => api.get(`/cloudflare/zones/${zoneId}/dns-records`),
  updateNsAtNamecheap: (zoneId) => api.post(`/cloudflare/zones/${zoneId}/update-nameservers-at-namecheap`),
  configureApexWww: (zoneId, body) => api.post(`/cloudflare/zones/${zoneId}/configure-apex-www`, body),

  getDomainStatus: (domain) => api.get(`/cloudflare/domains/${domain}/status`),
  onboardDomain: (domain, body) => api.post(`/cloudflare/domains/${domain}/onboard`, body),

  getActivityLogs: () => api.get("/cloudflare/activity-logs"),
};

// ─── Adspect API helpers ──────────────────────────────────────────────────────

export const adspectApi = {
  getConfig: () => api.get("/adspect/config"),
  saveConfig: (body) => api.post("/adspect/config", body),
  testConfig: () => api.post("/adspect/config/test"),

  getAccount: () => api.get("/adspect/account"),

  listStreams: () => api.get("/adspect/streams"),
  getStream: (id) => api.get(`/adspect/streams/${id}`),
  createStream: (body) => api.post("/adspect/streams", body),
  updateStream: (id, body) => api.patch(`/adspect/streams/${id}`, body),
  patchMode: (id, mode) => api.patch(`/adspect/streams/${id}`, { mode }),
  copyStream: (id) => api.post(`/adspect/streams/${id}/copy`),
  deleteStream: (id) => api.delete(`/adspect/streams/${id}`),

  downloadFile: async (streamId, filename) => {
    const res = await api.get(`/adspect/streams/${streamId}/files/${filename}`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  getFunnelReport: () => api.get("/adspect/reports/funnel"),

  getClickLog: (params) => api.get("/adspect/reports/log", { params }),
};

// ─── Quick Deploy API helpers ─────────────────────────────────────────────────

export const deployApi = {
  listDomains: () => api.get("/deploy/namecheap-domains"),
  listCpanelAccounts: () => api.get("/deploy/cpanel-accounts"),
  setupFilter: (body) => api.post("/deploy/setup-filter", body),
  getHistory: () => api.get("/deploy/history"),
};

export default api;
